import {
  AfterContentInit,
  Component,
  DoCheck,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subscription } from "rxjs";

import { CheckboxToggleComponent } from "./checkbox-toggle.component";
import { CheckboxComponent } from "./checkbox.component";

interface CheckboxSubscriptions {
  checkedChange?: Subscription;
  touchedChange?: Subscription;
}

@Component({
  selector: "ta-checkbox-group",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxGroupComponent),
      multi: true
    }
  ],
  template: `
    <ng-content></ng-content>
  `
})
export class CheckboxGroupComponent<T>
  implements AfterContentInit, DoCheck, OnDestroy, ControlValueAccessor {
  @HostBinding("class.checkbox-group")
  get hostClass(): boolean {
    return true;
  }

  @Input()
  get value(): T[] {
    return this._value;
  }
  set value(value: T[]) {
    // TODO: do shallow checking?
    if (areArrayValuesEqual(this._value, value)) {
      return;
    }

    this.valueSet = new Set(value);
    if (this.valueSet.size !== value.length) {
      // dedupe values
      value = Array.from(value);
    }
    this._value = value;

    this.valueChange.emit(value);
  }
  private _value: T[] = [];
  private valueSet: Set<T> = new Set();

  @Output() valueChange: EventEmitter<T[]> = new EventEmitter<T[]>();

  @HostBinding("class.checkbox-group--touched")
  get touched(): boolean {
    return this._touched;
  }
  set touched(value: boolean) {
    value = value != null && value !== false;
    if (this._touched === value) {
      return;
    }
    this._touched = value;
    this.touchedChange.emit(value);
  }
  private _touched: boolean = false;

  @Output() touchedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  @HostBinding("class.checkbox-group--disabled")
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value != null && value !== false;
  }
  private _disabled: boolean;

  constructor() {
    this.initControlAccessors();
  }

  isSelected(value: T): boolean {
    return this.valueSet.has(value);
  }

  toggleGroup(group: string, checked?: boolean): void {
    // get checkboxes in toggle group
    let checkboxes: CheckboxComponent[] = Array.from(this.checkboxes)
      .filter((checkbox) => checkbox.isInToggleGroup(group))
      .filter((checkbox) => !checkbox.disabled);
    if (checkboxes.length === 0) {
      // no checkboxes in toggle group
      return;
    }

    if (checked == null) {
      // if no target state specified, check all if none checked, uncheck all if some checked
      checked = checkboxes.some((checkbox) => checkbox.checked);
    }

    // find the values affected by the change
    let changedValues: T[] = checkboxes
      .map((checkbox) => {
        return checkbox.value as T;
      })
      .filter((value) => {
        return this.valueSet.has(value) !== checked;
      });

    if (changedValues.length === 0) {
      return;
    }

    if (checked) {
      // add changed values to value and valueSet
      changedValues.forEach((value) => {
        this.valueSet.add(value);
        this.value = [...this.value, value];
      });
    } else {
      // remove changed values from value and valueSet
      changedValues.forEach((value) => {
        let i: number = this.value.indexOf(value);
        this.valueSet.delete(value);
        this.value = [
          ...this.value.slice(0, i),
          ...this.value.slice(i + 1, this.value.length)
        ];
      });
    }
    this.valueChange.emit(this.value);
  }

  toggleAll(checked?: boolean): void {
    if (checked == null) {
      // if no target state specified, check all if none checked, uncheck all if some checked
      checked = this.value.length === 0;
    }

    if (checked) {
      this.value = Array.from(this.checkboxes)
        .filter((checkbox) => !checkbox.disabled)
        .map((checkbox) => checkbox.value);
    } else {
      this.value = [];
    }
  }

  /**
   * @internal
   */
  checkboxes: Set<CheckboxComponent> = new Set<CheckboxComponent>();

  /**
   * @internal
   */
  checkboxToggles: Set<CheckboxToggleComponent> = new Set<
    CheckboxToggleComponent
  >();

  private checkboxToggleMap: Map<
    string | undefined,
    CheckboxToggleComponent
  > = new Map<string, CheckboxToggleComponent>();
  private checkboxSubscriptions: Map<
    CheckboxComponent,
    CheckboxSubscriptions
  > = new Map<CheckboxComponent, CheckboxSubscriptions>();

  private _initialized: boolean = false;
  ngDoCheck(): void {
    if (!this._initialized) {
      this._initialized = true;
      this.checkboxToggles.forEach((checkboxToggle) => {
        checkboxToggle.updateState();
      });
    }
  }

  ngAfterContentInit(): void {
    if (!this.touched) {
      let touchedChangeSubscription: Subscription = this.touchedChange.subscribe(
        () => {
          this.checkboxes.forEach((checkbox) => {
            let checkboxSubscriptions:
              | CheckboxSubscriptions
              | undefined = this.checkboxSubscriptions.get(checkbox);
            if (
              checkboxSubscriptions == null ||
              checkboxSubscriptions.touchedChange == null
            ) {
              return;
            }
            checkboxSubscriptions.touchedChange.unsubscribe();
            delete checkboxSubscriptions.touchedChange;
          });
          touchedChangeSubscription.unsubscribe();
        }
      );
    }

    this.valueChange.subscribe(() => {
      this.checkboxes.forEach((checkbox) => {
        checkbox.checked = this.valueSet.has(checkbox.value);
      });
    });
  }

  ngOnDestroy(): void {
    this.checkboxes.forEach((checkbox) => {
      this.removeCheckbox(checkbox, true);
    });
    this.checkboxToggles.forEach((checkboxToggle) => {
      this.removeCheckboxToggle(checkboxToggle);
    });
  }

  /**
   * @internal
   */
  addCheckbox(checkbox: CheckboxComponent): void {
    if (this.checkboxes.has(checkbox)) {
      return;
    }
    this.checkboxes.add(checkbox);

    checkbox.checked = this.valueSet.has(checkbox.value);

    // update toggle groups
    (checkbox.toggleGroups as string[])
      .map((group) => {
        return this.checkboxToggleMap.get(group);
      })
      .filter((checkboxToggle) => {
        return checkboxToggle != null;
      })
      .forEach((checkboxToggle: CheckboxToggleComponent) => {
        checkboxToggle.updateState();
      });

    let allCheckboxToggle:
      | CheckboxToggleComponent
      | undefined = this.checkboxToggleMap.get(undefined);
    if (allCheckboxToggle != null) {
      allCheckboxToggle.updateState();
    }

    // subscribe to checked change
    let subscriptions: CheckboxSubscriptions = {};
    subscriptions.checkedChange = checkbox.checkedChange.subscribe(() => {
      this.updateValueFromCheckbox(checkbox);
    });

    if (!this.touched) {
      subscriptions.touchedChange = checkbox.touchedChange.subscribe(() => {
        this.touched = true;
      });
    }

    this.checkboxSubscriptions.set(checkbox, subscriptions);
  }

  /**
   * @internal
   */
  removeCheckbox(
    checkbox: CheckboxComponent,
    onGroupDestroy: boolean = false
  ): void {
    if (!this.checkboxes.has(checkbox)) {
      return;
    }
    if (!onGroupDestroy && this.valueSet.has(checkbox.value)) {
      // if checkbox value is in checkbox group value, remove it
      let i: number = this.value.indexOf(checkbox.value);
      this.value.splice(i, 1);
      this.valueSet.delete(checkbox.value);
      this.valueChange.emit(this.value);
    }
    this.checkboxes.delete(checkbox);

    // cleanup subscriptions
    let subscriptions:
      | CheckboxSubscriptions
      | undefined = this.checkboxSubscriptions.get(checkbox);
    if (subscriptions == null) {
      return;
    }
    this.checkboxSubscriptions.delete(checkbox);

    (["checkedChange", "touchedChange"] as Array<
      keyof CheckboxSubscriptions
    >).forEach((key) => {
      let subscription: Subscription | undefined = subscriptions![key];
      if (subscription == null) {
        return;
      }
      subscription.unsubscribe();
      delete subscriptions![key];
    });
  }

  /**
   * @internal
   */
  addCheckboxToggle(checkboxToggle: CheckboxToggleComponent): void {
    if (this.checkboxToggles.has(checkboxToggle)) {
      return;
    }
    this.checkboxToggles.add(checkboxToggle);
    checkboxToggle.updateState();

    this.checkboxToggleMap.set(
      checkboxToggle.toggleAll ? undefined : checkboxToggle.toggle,
      checkboxToggle
    );
  }

  /**
   * @internal
   */
  removeCheckboxToggle(checkboxToggle: CheckboxToggleComponent): void {
    if (!this.checkboxToggles.has(checkboxToggle)) {
      return;
    }
    this.checkboxToggles.delete(checkboxToggle);
  }

  private updateValueFromCheckbox(checkbox: CheckboxComponent): void {
    if (checkbox.checked === this.valueSet.has(checkbox.value)) {
      // checkbox checked state is consistent with current value set, no need to do anything
      return;
    }

    // insert/remove from value and valueSet
    if (checkbox.checked) {
      this.valueSet.add(checkbox.value);
      this.value = [...this.value, checkbox.value];
    } else {
      this.valueSet.delete(checkbox.value);
      const i = this.value.indexOf(checkbox.value);
      this.value = [
        ...this.value.slice(0, i),
        ...this.value.slice(i + 1, this.value.length)
      ];
    }
  }

  initControlAccessors(): void {
    this.valueChange.subscribe((value) => {
      if (this._onChangeFn == null) {
        return;
      }
      this._onChangeFn(value);
    });

    let touchSubscription: Subscription = this.touchedChange.subscribe(() => {
      if (this._onTouchedFn != null) {
        this._onTouchedFn();
      }
      touchSubscription.unsubscribe();
    });
  }

  writeValue(value?: T[]): void {
    this.value = value != null ? value : [];
  }

  private _onChangeFn?: (value: T[]) => void;
  registerOnChange(fn: (value: T[]) => void): void {
    this._onChangeFn = fn;
  }

  private _onTouchedFn?: () => void;
  registerOnTouched(fn: () => void): void {
    this._onTouchedFn = fn;
  }
}

export function areArrayValuesEqual(
  arr1: any[] = [],
  arr2: any[] = []
): boolean {
  if (arr1 === arr2) {
    return true;
  }

  let set1: Set<any> = new Set(arr1),
    set2: Set<any> = new Set(arr2);

  if (set1.size !== set2.size) {
    return false;
  }

  return Array.from(set1).every((val) => set2.has(val));
}
